import { t } from "../i18n/index.js";
import {
  BINDABLE_ACTIONS,
  DEFAULT_CONTROLS,
  assignKey,
  currentControls,
  isReservedKey,
  keyLabel,
  onControlsChange,
  primeKeyLabels,
  saveControls,
  unboundActions,
  type BindableAction,
  type ControlSettings,
} from "../input/bindings.js";
import "./controlsDialog.css";

/**
 * CONTROLES: cambiar las teclas, a pantalla propia pero con la cara de un
 * dialogo (se entra por un boton pequeño del menu, no por una pagina de
 * ajustes). Cada accion tiene principal y alternativa; se pulsa el hueco y
 * luego la tecla. Se guarda al momento: no hay "aplicar" que olvidar.
 */
const ACTION_LABEL: Record<BindableAction, () => string> = {
  moveUp: () => t("Arriba"),
  moveDown: () => t("Abajo"),
  moveLeft: () => t("Izquierda"),
  moveRight: () => t("Derecha"),
  kick: () => t("Golpear"),
  dash: () => t("Regate · cuerpo · sprint"),
};

const ACTION_HINT: Partial<Record<BindableAction, () => string>> = {
  kick: () => t("toque pasa, mantener dispara"),
};

export function openControlsDialog(root: HTMLElement = document.body): Promise<void> {
  void primeKeyLabels();
  let settings: ControlSettings = currentControls();
  /** El hueco que espera tecla, si hay alguno. */
  let listening: { action: BindableAction; slot: 0 | 1 } | null = null;
  let notice = "";

  const overlay = document.createElement("div");
  overlay.className = "controls-dialog";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-labelledby", "controls-dialog-title");
  overlay.innerHTML = `
    <div class="controls-dialog-backdrop" data-cd-close></div>
    <div class="controls-dialog-sheet">
      <h2 id="controls-dialog-title">${t("CONTROLES")}</h2>
      <p class="controls-dialog-lead">${t("Pulsa una casilla y luego la tecla. Supr la vacía, Esc cancela.")}</p>
      <div class="controls-dialog-grid" data-grid></div>
      <p class="controls-dialog-notice" data-notice aria-live="polite"></p>
      <label class="controls-dialog-toggle">
        <input type="checkbox" data-mouse />
        <span>
          <strong>${t("Golpear con el ratón")}</strong>
          <small>${t("Clic izquierdo: golpeas hacia el cursor, al grado. El teclado sigue funcionando.")}</small>
        </span>
      </label>
      <p class="controls-dialog-pad">${t("Justo después de chutar, mantén una dirección hacia un lado y el balón se dobla hacia allí.")}</p>
      <p class="controls-dialog-pad">${t("Mando: stick o cruceta para moverte · A golpear · X regatear.")}</p>
      <div class="controls-dialog-actions">
        <button type="button" class="controls-dialog-reset" data-cd-reset>${t("Restablecer")}</button>
        <button type="button" class="controls-dialog-ok" data-cd-close>${t("Listo")}</button>
      </div>
    </div>`;
  root.appendChild(overlay);

  const grid = overlay.querySelector<HTMLElement>("[data-grid]")!;
  const noticeEl = overlay.querySelector<HTMLElement>("[data-notice]")!;
  const mouse = overlay.querySelector<HTMLInputElement>("[data-mouse]")!;

  const render = (): void => {
    grid.innerHTML = "";
    for (const action of BINDABLE_ACTIONS) {
      const row = document.createElement("div");
      row.className = "controls-dialog-row";
      const name = document.createElement("span");
      name.className = "controls-dialog-action";
      name.textContent = ACTION_LABEL[action]();
      const hint = ACTION_HINT[action];
      if (hint) {
        const small = document.createElement("small");
        small.textContent = hint();
        name.appendChild(small);
      }
      row.appendChild(name);
      for (const slot of [0, 1] as const) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "controls-dialog-key";
        const waiting = listening?.action === action && listening.slot === slot;
        btn.classList.toggle("is-listening", waiting);
        btn.classList.toggle("is-empty", !settings.keys[action][slot]);
        btn.textContent = waiting ? t("Pulsa…") : keyLabel(settings.keys[action][slot]);
        btn.setAttribute(
          "aria-label",
          `${ACTION_LABEL[action]()} · ${slot === 0 ? t("principal") : t("alternativa")}: ${keyLabel(settings.keys[action][slot])}`,
        );
        btn.addEventListener("click", () => {
          listening = { action, slot };
          notice = "";
          render();
        });
        row.appendChild(btn);
      }
      grid.appendChild(row);
    }
    const missing = unboundActions(settings);
    noticeEl.textContent =
      notice || (missing.length ? t("Sin tecla: {actions}", { actions: missing.map((a) => ACTION_LABEL[a]()).join(", ") }) : "");
    mouse.checked = settings.mouseAim;
    if (listening) grid.querySelector<HTMLButtonElement>(".is-listening")?.focus({ preventScroll: true });
  };

  const commit = (next: ControlSettings): void => {
    settings = next;
    saveControls(settings);
    render();
  };

  const unsub = onControlsChange(() => {
    settings = currentControls();
    render();
  });
  render();

  return new Promise((resolve) => {
    const close = (): void => {
      window.removeEventListener("keydown", onKey, true);
      unsub();
      overlay.remove();
      resolve();
    };

    /*
     * En fase de CAPTURA y cortando la propagacion: mientras se espera una
     * tecla, ninguna otra escucha (el menu tiene atajos con letras, la O entra
     * al online) puede llevarsela.
     */
    const onKey = (event: KeyboardEvent): void => {
      if (!listening) {
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          close();
        }
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (event.repeat) return;
      const { action, slot } = listening;
      if (event.code === "Escape") {
        listening = null;
        notice = "";
        render();
        return;
      }
      if (event.code === "Delete" || event.code === "Backspace") {
        listening = null;
        commit(assignKey(settings, action, slot, null));
        return;
      }
      if (isReservedKey(event.code)) {
        notice = t("{key} ya es del juego (salir, estadísticas o chat). Elige otra.", { key: keyLabel(event.code) });
        render();
        return;
      }
      listening = null;
      notice = "";
      commit(assignKey(settings, action, slot, event.code));
    };
    window.addEventListener("keydown", onKey, true);

    for (const el of overlay.querySelectorAll("[data-cd-close]")) el.addEventListener("click", close);
    overlay.querySelector("[data-cd-reset]")!.addEventListener("click", () => {
      listening = null;
      notice = "";
      commit(DEFAULT_CONTROLS);
    });
    mouse.addEventListener("change", () => commit({ ...settings, mouseAim: mouse.checked }));
    overlay.querySelector<HTMLButtonElement>(".controls-dialog-ok")!.focus({ preventScroll: true });
  });
}
